import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import Toaster from 'components/Toaster';
import { useMe } from 'hooks/useMe';
import storage from 'shared/storage';

const PageContainer = styled.div`
  min-height: 100vh;
  background: ${(props) =>
    props.theme?.colors?.background?.primary ||
    (props.theme?.name === 'dark'
      ? 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%)'
      : 'rgb(248, 250, 252)')};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${(props) => props.theme?.spacing?.xl || '32px'};
  transition: background
    ${(props) => props.theme?.transitions?.theme || '300ms ease-in-out'};

  @media (max-width: 768px) {
    padding: ${(props) => props.theme?.spacing?.md || '16px'};
  }
`;

const ContentCard = styled.div`
  background: ${(props) =>
    props.theme?.colors?.card?.background ||
    (props.theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(255, 255, 255, 0.95)')};
  border: 1px solid
    ${(props) =>
      props.theme?.colors?.card?.border ||
      (props.theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgb(226, 232, 240)')};
  border-radius: ${(props) => props.theme?.borderRadius?.lg || '12px'};
  box-shadow: ${(props) =>
    props.theme?.colors?.card?.shadow ||
    (props.theme?.name === 'dark'
      ? 'rgba(0, 0, 0, 0.2) 0px 1px 3px'
      : 'rgba(0, 0, 0, 0.1) 0px 1px 3px')};
  padding: ${(props) => props.theme?.spacing?.xxl || '48px'};
  max-width: 500px;
  width: 100%;
  text-align: center;
  transition: all
    ${(props) => props.theme?.transitions?.theme || '300ms ease-in-out'};

  @media (max-width: 768px) {
    padding: ${(props) => props.theme?.spacing?.xl || '32px'};
  }
`;

const SuccessIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #22c55e 0%, #4ade80 100%);
  border-radius: ${(props) => props.theme?.borderRadius?.round || '50%'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${(props) => props.theme?.spacing?.lg || '24px'};
  box-shadow: 0 8px 24px rgba(34, 197, 94, 0.3);
  animation: scaleIn 0.5s ease-out;

  @keyframes scaleIn {
    from {
      transform: scale(0);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }

  svg {
    width: 40px;
    height: 40px;
    color: #ffffff;
    animation: checkmark 0.5s ease-out 0.3s both;
  }

  @keyframes checkmark {
    from {
      stroke-dasharray: 100;
      stroke-dashoffset: 100;
    }
    to {
      stroke-dasharray: 100;
      stroke-dashoffset: 0;
    }
  }
`;

const LoadingIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #fa4616 0%, #ff6b3d 100%);
  border-radius: ${(props) => props.theme?.borderRadius?.round || '50%'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${(props) => props.theme?.spacing?.lg || '24px'};
  box-shadow: 0 8px 24px rgba(250, 70, 22, 0.3);
  animation: pulse 2s ease-in-out infinite;

  @keyframes pulse {
    0%,
    100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.05);
    }
  }

  svg {
    width: 40px;
    height: 40px;
    color: #ffffff;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: ${(props) =>
    props.theme?.colors?.text?.primary ||
    (props.theme?.name === 'dark' ? '#ffffff' : '#000000')};
  margin: 0 0 ${(props) => props.theme?.spacing?.sm || '8px'} 0;
  transition: color
    ${(props) => props.theme?.transitions?.theme || '300ms ease-in-out'};

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const Message = styled.p`
  font-size: 15px;
  color: ${(props) =>
    props.theme?.colors?.text?.secondary ||
    (props.theme?.name === 'dark' ? '#94a3b8' : '#64748b')};
  margin: 0 0 ${(props) => props.theme?.spacing?.xl || '32px'} 0;
  line-height: 1.6;
  transition: color
    ${(props) => props.theme?.transitions?.theme || '300ms ease-in-out'};
`;

const LoadingWrapper = styled.div`
  margin-top: ${(props) => props.theme?.spacing?.lg || '24px'};
`;

const CountdownText = styled.p`
  font-size: 13px;
  color: ${(props) =>
    props.theme?.colors?.text?.muted ||
    (props.theme?.name === 'dark' ? '#64748b' : '#94a3b8')};
  margin-top: ${(props) => props.theme?.spacing?.md || '16px'};
  transition: color
    ${(props) => props.theme?.transitions?.theme || '300ms ease-in-out'};
`;

const WaveBars = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
`;

const WaveBar = styled.span`
  width: 8px;
  height: 60px;
  background: linear-gradient(180deg, #fa4616 0%, #ff6b3d 100%);
  border-radius: 4px;
  animation: wave 1.2s ease-in-out infinite;
  animation-delay: ${(props) => props.delay || '0s'};

  @keyframes wave {
    0%,
    40%,
    100% {
      transform: scaleY(0.4);
    }
    20% {
      transform: scaleY(1);
    }
  }
`;

/**
 * Handles Stripe Connect onboarding return
 * When user completes or exits the onboarding process
 */
export const StripeConnectReturn = () => {
  const history = useHistory();
  const [isChecking, setIsChecking] = useState(true);
  const [countdown, setCountdown] = useState(3);
  const [stripeActivated, setStripeActivated] = useState(false);
  const [toaster, setToaster] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  });

  const { data: meData, refetch } = useMe();

  useEffect(() => {
    let isMounted = true;
    let countdownInterval = null;
    let redirectTimeout = null;

    const checkStripeStatus = async () => {
      try {
        // Get account ID from storage and parse as integer
        const accountId = parseInt(storage.get('accountId'));

        // Poll multiple times to wait for Stripe webhooks to process
        const maxRetries = 5;
        const retryDelay = 2000; // 2 seconds between retries
        let stripeIsActive = false;

        for (let attempt = 0; attempt < maxRetries; attempt++) {
          if (!isMounted) return;

          // Refetch to get latest data
          await refetch();

          if (meData?.me?.accounts) {
            const currentAccount = meData.me.accounts.find(
              (account) => account.id === accountId
            );

            if (currentAccount?.stripe_active) {
              stripeIsActive = true;
              break; // Exit loop, we found it's active
            }
          }

          // Wait before next retry (unless this was the last attempt)
          if (attempt < maxRetries - 1) {
            await new Promise((resolve) => setTimeout(resolve, retryDelay));
          }
        }

        if (!isMounted) return;

        if (stripeIsActive) {
          // Stripe setup completed successfully
          setStripeActivated(true);
          setToaster({
            isOpen: true,
            type: 'success',
            title: 'Stripe Setup Complete!',
            message: 'You can now accept payments and receive payouts.',
          });

          // Clear the onboarding modal flag so it doesn't show again
          storage.set('hasShownOnboardingModal', true);
        } else {
          // User may have exited without completing or webhooks still processing
          setStripeActivated(false);
          setToaster({
            isOpen: true,
            type: 'error',
            title: 'Setup Not Completed',
            message:
              'You can finish your Stripe setup anytime from your account settings.',
          });
        }

        // Mark checking as complete
        if (!isMounted) return;
        setIsChecking(false);

        // Start countdown
        countdownInterval = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              if (countdownInterval) {
                clearInterval(countdownInterval);
              }
              if (isMounted) {
                history.push('/admin-track/home');
              }
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } catch (error) {
        console.error('Error checking Stripe status:', error);

        if (!isMounted) return;

        setToaster({
          isOpen: true,
          type: 'error',
          title: 'Verification Failed',
          message: 'Unable to verify Stripe setup status.',
        });

        setIsChecking(false);

        redirectTimeout = setTimeout(() => {
          if (isMounted) {
            history.push('/admin-track/home');
          }
        }, 2000);
      }
    };

    checkStripeStatus();

    return () => {
      isMounted = false;
      if (countdownInterval) {
        clearInterval(countdownInterval);
      }
      if (redirectTimeout) {
        clearTimeout(redirectTimeout);
      }
    };
  }, [refetch, history, meData]);

  return (
    <EnhancedThemeProvider>
      <PageContainer>
        <ContentCard>
          {isChecking ? (
            <>
              <LoadingIcon>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </LoadingIcon>

              <Title>Verifying Stripe Setup</Title>
              <Message>
                Please wait while we confirm your Stripe account status...
              </Message>

              <LoadingWrapper>
                <WaveBars>
                  <WaveBar delay="-0.4s" />
                  <WaveBar delay="-0.3s" />
                  <WaveBar delay="-0.2s" />
                  <WaveBar delay="-0.1s" />
                  <WaveBar delay="0s" />
                </WaveBars>
              </LoadingWrapper>
            </>
          ) : stripeActivated ? (
            <>
              <SuccessIcon>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="3"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </SuccessIcon>

              <Title>Stripe Setup Complete!</Title>
              <Message>
                Your payment processing is now active. You're all set to accept
                payments and receive payouts.
              </Message>
              <CountdownText>
                Redirecting to your dashboard in {countdown} second
                {countdown !== 1 ? 's' : ''}...
              </CountdownText>
            </>
          ) : (
            <>
              <LoadingIcon>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </LoadingIcon>

              <Title>Setup Not Completed</Title>
              <Message>
                It looks like you haven't finished setting up your Stripe
                account yet. You can complete it anytime from your account
                settings.
              </Message>
              <CountdownText>
                Redirecting to your dashboard in {countdown} second
                {countdown !== 1 ? 's' : ''}...
              </CountdownText>
            </>
          )}
        </ContentCard>

        <Toaster
          isOpen={toaster.isOpen}
          onClose={() => setToaster({ ...toaster, isOpen: false })}
          title={toaster.title}
          message={toaster.message}
          type={toaster.type}
          duration={5000}
        />
      </PageContainer>
    </EnhancedThemeProvider>
  );
};
